import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { fetchMyPermissions, type ScopeType } from "@/lib/accessControl";

/**
 * The signed-in user's permissions, from my_access_permissions(). Use `can`
 * to show or hide UI; the database enforces the same rules through
 * has_permission(), so this is never the only check.
 *
 * `can(key)` is true for an unscoped grant. `can(key, type, id)` is also true
 * for a grant scoped to exactly that region, program or course.
 */
export function useAccessPermissions() {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ["my_access_permissions", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: fetchMyPermissions,
  });

  const can = useCallback(
    (key: string, scopeType?: ScopeType, scopeId?: string) =>
      (query.data ?? []).some(
        (p) =>
          p.permission_key === key &&
          (p.scope_type === null || (p.scope_type === scopeType && p.scope_id === scopeId)),
      ),
    [query.data],
  );

  return { ...query, can };
}
